import * as React from 'react';
import * as classnames from 'classnames';
import * as _ from 'lodash';
import { UilTimes } from '@iconscout/react-unicons';

import { Audience, Panel, Project, PROJECT_STATUS } from '@nexxt/common/types';
import Toggle from '../../../common/Toggle';
import { defaultPanelSource, emptyPanelSource } from '../defaultValues';
import PanelSource from './PanelSource/PanelSource';
import ValidationService from '@nexxt/common/services/ValidationService';
import { getConfirmationBox } from '../../../common/swal/Sweetalert';
import { alertMessage } from '../../../../constants/alertMessage';

interface PanelSourceSettingsProps {
    audience: Audience;
    validationService: ValidationService;
    currentProject: Project;
    updatePanelSettings: (index: number, panel: Panel) => void;
    disableByFieldName: (keyName?: string) => boolean;
}

const PanelSourceSettings: React.FC<PanelSourceSettingsProps> = ({
    audience,
    validationService,
    currentProject,
    updatePanelSettings,
    disableByFieldName
}: PanelSourceSettingsProps) => {
    const [activePanelIndex, setActivePanelIndex] = React.useState<number>(0);
    const [errorObject, setErrorObject] = React.useState({});

    React.useEffect(() => {
        setErrorObject(validationService.validatePanelSources(audience.panels));
    }, [audience.panels]);

    const updatePanelSettingsByKeyName = (index, keyName, value) => {
        let currentPanel;
        if (keyName === 'name') {
            currentPanel = {
                ...audience.panels[index],
                [keyName]: value,
                key: _.snakeCase(value as string).toUpperCase()
            };
        } else {
            currentPanel = { ...audience.panels[index], [keyName]: value };
        }
        updatePanelSettings(index, currentPanel);
    };

    const addNewPanelSource = () => {
        updatePanelSettings(audience.panels.length, emptyPanelSource);
        setActivePanelIndex(audience.panels.length);
    };

    const deletePanelSource = async (index) => {
        const result = await getConfirmationBox({
            message: alertMessage.CONFIRM_CONTINUE,
            showCloseButton: true,
            confirmButtonColor: '',
            title: alertMessage.UNDO_CURRENT_CHANGE
        });

        if (result.isConfirmed) {
            setActivePanelIndex(
                audience.panels.length - 1 === activePanelIndex
                    ? activePanelIndex - 1
                    : activePanelIndex
            );
            updatePanelSettings(index, null);
        }
    };
    const availablePanels = audience.panels || [defaultPanelSource];
    const panelNames = availablePanels.map((panel) => panel.name);
    const disabled = disableByFieldName('self-provided-panel');
    const hasProjectLaunched = currentProject.status !== PROJECT_STATUS.DRAFT;
    return (
        <div className="audience--panel-sourcing--container">
            <div className="audience--panel-sourcing--title-container">
                <span className="audience--panel-sourcing--title">
                    Self-Provided Settings
                </span>
                <div>
                    <Toggle
                        label="Show Advanced mode"
                        checked={
                            audience.panels[activePanelIndex].advancedSettings
                        }
                        onChange={() =>
                            updatePanelSettingsByKeyName(
                                activePanelIndex,
                                'advancedSettings',
                                !audience.panels[activePanelIndex]
                                    .advancedSettings
                            )
                        }
                        disabled={disabled}
                    />
                </div>
            </div>
            <div className="panel-source--list">
                {panelNames.map((panelName, i) => (
                    <span
                        className={classnames('panel-source--list-item', {
                            active: i === activePanelIndex,
                            error: errorObject?.[i]?.isError
                        })}
                        key={`${panelName}-${i}`}
                        onClick={() => setActivePanelIndex(i)}
                    >
                        {panelName || 'New Panel'}

                        {i ? (
                            <UilTimes
                                size={15}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    deletePanelSource(i);
                                }}
                            />
                        ) : null}
                    </span>
                ))}
                <span
                    className={classnames('panel-source--list-item', {
                        'add-item': true,
                        disabled
                    })}
                    onClick={addNewPanelSource}
                >
                    Add a new panel
                </span>
            </div>

            <PanelSource
                index={activePanelIndex}
                key={activePanelIndex}
                panel={availablePanels[activePanelIndex]}
                updatePanelSettingsByKeyName={(keyName, value) =>
                    updatePanelSettingsByKeyName(
                        activePanelIndex,
                        keyName,
                        value
                    )
                }
                errorObject={errorObject?.[activePanelIndex]}
                projectId={audience.projectId}
                currentProject={currentProject}
                disabled={disableByFieldName('self-provided-panel')}
            />
        </div>
    );
};

export default PanelSourceSettings;
